import React, { useRef } from "react";
import { FormGroup, Label, FormFeedback } from "reactstrap";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

interface GoogleMapPickerProps {
  value: {
    name?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
  };
  onChange: (location: {
    name?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
    addressLine1?: string;
    addressLine2?: string;
    country?: string;
    state?: string;
    city?: string;
    zipCode?: string;
  }) => void;
  onBlur?: () => void;
  invalid?: boolean;
  error?: string;
}

const libraries: ("places")[] = ["places"];

const GoogleMapPicker: React.FC<GoogleMapPickerProps> = ({
  value,
  onChange,
  onBlur,
  invalid = false,
  error,
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Let the library load Google Maps SDK
  const { isLoaded } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries,
  });

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place || !place.geometry) return;

    const lat = place.geometry.location?.lat();
    const lng = place.geometry.location?.lng();
    const formattedAddress = place.formatted_address || "";

    const get = (type: string) =>
      place.address_components?.find((c) => c.types.includes(type))?.long_name || "";
    onChange({
      name: place.name || "",
      latitude: lat,
      longitude: lng,
      formattedAddress,
      addressLine1: formattedAddress,
      addressLine2: get("subpremise"),
      city: get("locality"),
      state: get("administrative_area_level_1"),
      country: get("country"),
      zipCode: get("postal_code"),
    });
  };

  if (!isLoaded) {
    return (
      <FormGroup>
        <Label className="form-label">Location</Label>
        <div className="form-control">Loading Google Maps…</div>
      </FormGroup>
    );
  }

  return (
    <FormGroup>
      <Label className="form-label">Location</Label>
      <Autocomplete
        onLoad={(ac) => (autocompleteRef.current = ac)}
        onPlaceChanged={handlePlaceChanged}
      >
        <input
          type="text"
          placeholder="Search for a location or business…"
          className={`form-control${invalid ? " is-invalid" : ""}`}
          onBlur={onBlur}
        />
      </Autocomplete>

      {error && <FormFeedback type="invalid">{error}</FormFeedback>}
    </FormGroup>
  );
};

export default GoogleMapPicker;
